/**
 * GeolocationClient - TypeScript SDK for Vesant Geolocation & Compliance Service
 *
 * Provides type-safe methods to interact with the geolocation service API.
 * Extends BaseClient for consistent retry logic, timeout handling, and error management.
 */

import { BaseClient } from '../core/client';
import { ValidationError } from '../core/errors';
import { generateDeviceId, getBrowserInfo } from '../shared/browser-utils';
import type { BaseClientConfig, RequestOptions } from '../core/config';
import type {
  GeolocationClientConfig,
  GeolocationConfigResponse,
  VerifyIPRequest,
  LocationVerification,
  ComplianceCheckResponse,
  PaginationParams,
  // Location Request Types
  LocationRequest,
  CreateLocationRequestRequest,
  LocationRequestResult,
  LocationRequestFilters,
  LocationRequestListResponse,
  ResendLocationRequestRequest,
  LocationShareInfo,
  LocationCaptureRequest,
  LocationCaptureResponse,
  // CipherText Types
  ValidateCipherTextRequest,
  ValidateCipherTextResponse,
  CipherTextReason,
  CipherTextCustomerData,
  CipherTextOptions,
  CipherTextResult,
} from './types';
import { generateCipherText as generateCipherTextUtil } from './ciphertext';

/**
 * GeolocationClient extends BaseClient for:
 * - Consistent retry logic with exponential backoff
 * - Unified error handling (VesantError, NetworkError, TimeoutError, etc.)
 * - Automatic timeout management
 * - Debug logging
 *
 * All geolocation verification calls use requestWithRetry() to handle
 * transient failures gracefully.
 */
export class GeolocationClient extends BaseClient {
  private cachedSigningKey?: string;

  constructor(config: GeolocationClientConfig) {
    // Convert GeolocationClientConfig to BaseClientConfig
    const baseConfig: BaseClientConfig = {
      baseURL: config.baseURL,
      tenantId: config.tenantId,
      apiKey: config.apiKey,
      headers: config.headers,
      timeout: config.timeout,
      retries: 3, // Default retry count for geolocation calls
      debug: config.debug,
      logger: config.logger,
      environment: config.environment,
    };
    super(baseConfig);
  }

  // ============================================================================
  // IP Verification & Compliance
  // ============================================================================

  /**
   * Verify an IP address and check compliance
   *
   * Uses requestWithRetry() for automatic retry on transient failures.
   *
   * @param request - Verification request with IP, user ID, event type, and optional device fingerprint
   * @returns Location verification result with risk assessment
   *
   * @example
   * ```typescript
   * const result = await client.verifyIP({
   *   ip_address: "8.8.8.8",
   *   user_id: "user_123",
   *   event_type: "login",
   *   device_fingerprint: {
   *     device_id: "device_abc",
   *     user_agent: navigator.userAgent,
   *     platform: "web"
   *   }
   * });
   *
   * if (result.is_blocked) {
   *   console.log("Access blocked:", result.risk_reasons);
   * }
   * ```
   */
  async verifyIP(request: VerifyIPRequest, requestOptions?: RequestOptions): Promise<LocationVerification> {
    if (!request.ip_address?.trim()) {
      throw new ValidationError('ip_address is required and must be a non-empty string', ['ip_address']);
    }

    // Auto-collect device fingerprint when not provided by the caller.
    // This ensures device trust detection (new device, fingerprint changes)
    // works even when the integrator doesn't explicitly pass device data.
    const enrichedRequest = request.device_fingerprint
      ? request
      : { ...request, device_fingerprint: collectDeviceFingerprint() };

    // Use requestWithRetry for critical verification calls
    return this.requestWithRetry<LocationVerification>('/api/v1/geo/verify', {
      method: 'POST',
      body: JSON.stringify(enrichedRequest),
    }, undefined, undefined, requestOptions);
  }

  /**
   * Check compliance for a specific country
   *
   * @param countryISO - ISO 3166-1 alpha-2 country code (e.g., "US", "GB")
   * @returns Jurisdiction configuration and compliance status
   *
   * @example
   * ```typescript
   * const compliance = await client.checkCompliance("KP"); // North Korea
   * if (!compliance.is_compliant) {
   *   console.log("Country is not allowed:", compliance.jurisdiction?.status);
   * }
   * ```
   */
  async checkCompliance(countryISO: string, requestOptions?: RequestOptions): Promise<ComplianceCheckResponse> {
    return this.requestWithRetry<ComplianceCheckResponse>(
      `/api/v1/geo/check-compliance`,
      {
        method: 'POST',
        body: JSON.stringify({ country_iso: countryISO }),
      }, undefined, undefined, requestOptions
    );
  }

  // ============================================================================
  // GPS Configuration
  // ============================================================================

  /**
   * Get the tenant's GPS requirement configuration
   *
   * Returns which event types require GPS location to be collected.
   * Use this to auto-enable GPS collection in generateCipherText when required.
   *
   * @returns GPS requirement config per event type
   *
   * @example
   * ```typescript
   * const config = await client.getGPSConfig();
   * if (config.require_gps.login) {
   *   // GPS is required for login — auto-enable location
   * }
   * ```
   */
  async getGPSConfig(requestOptions?: RequestOptions): Promise<GeolocationConfigResponse> {
    const config = await this.requestWithRetry<GeolocationConfigResponse>('/api/v1/geo/config', undefined, undefined, undefined, requestOptions);
    if (config.signing_key) {
      this.cachedSigningKey = config.signing_key;
    }
    return config;
  }

  /**
   * Fetch the signing key from the dedicated authenticated endpoint.
   *
   * Falls back to getGPSConfig() for backward compatibility with older servers
   * that don't expose `/api/v1/geo/signing-key`.
   *
   * @returns The signing key string, or undefined if unavailable
   */
  async fetchSigningKey(requestOptions?: RequestOptions): Promise<string | undefined> {
    try {
      const response = await this.requestWithRetry<{ signing_key: string }>(
        '/api/v1/geo/signing-key', undefined, undefined, undefined, requestOptions,
      );
      if (response.signing_key) {
        this.cachedSigningKey = response.signing_key;
        return response.signing_key;
      }
    } catch {
      // Older servers may not have this endpoint — fall back to config
    }

    const config = await this.getGPSConfig(requestOptions);
    return config.signing_key;
  }

  // ============================================================================
  // CipherText Validation
  // ============================================================================

  /**
   * Validate a cipherText generated by the frontend SDK
   *
   * The cipherText contains encrypted device fingerprint and optional location data.
   * This method decrypts and validates the data, returning device info, location,
   * and risk assessment.
   *
   * @param cipherText - The encrypted cipherText string from generateCipherText()
   * @param userId - User ID associated with this verification
   * @param eventType - Reason for verification (login, registration, etc.)
   * @param expectedIP - Optional expected IP address for additional validation
   * @param customerData - Optional customer data for risk profile creation (used when user is blocked)
   * @returns Validation result with device info, location, and risk assessment
   *
   * @example
   * ```typescript
   * // Validate cipherText during registration with customer data
   * const result = await client.validateCipherText(
   *   cipherText,
   *   "user_123",
   *   "registration",
   *   clientIP,
   *   {
   *     full_name: "John Doe",
   *     email: "john@example.com",
   *     country: "US",
   *     state: "CA"
   *   }
   * );
   *
   * if (!result.valid) {
   *   console.log("CipherText validation failed:", result.errors);
   *   return;
   * }
   *
   * if (result.risk.is_blocked) {
   *   console.log("Access blocked:", result.risk.block_reasons);
   *   // A risk profile with full customer data has been created for the alert
   * }
   * ```
   */
  async validateCipherText(
    cipherText: string,
    userId: string,
    eventType: CipherTextReason,
    expectedIP?: string,
    customerData?: CipherTextCustomerData,
    requestOptions?: RequestOptions
  ): Promise<ValidateCipherTextResponse> {
    if (!cipherText?.trim()) {
      throw new ValidationError('cipherText is required and must be a non-empty string', ['cipherText']);
    }
    if (!userId?.trim()) {
      throw new ValidationError('userId is required and must be a non-empty string', ['userId']);
    }

    const request: ValidateCipherTextRequest = {
      cipher_text: cipherText,
      user_id: userId,
      event_type: eventType,
      expected_ip: expectedIP,
      customer_data: customerData,
    };

    return this.requestWithRetry<ValidateCipherTextResponse>(
      '/api/v1/geo/validate-ciphertext',
      {
        method: 'POST',
        body: JSON.stringify(request),
      }, undefined, undefined, requestOptions
    );
  }

  /**
   * Validate cipherText and verify IP in a single call
   *
   * Combines cipherText validation with IP verification for complete
   * location and device verification in one request.
   *
   * @param cipherText - The encrypted cipherText string
   * @param ipAddress - Client IP address
   * @param userId - User ID
   * @param eventType - Event type (login, registration, etc.)
   * @returns Combined validation and verification result
   *
   * @example
   * ```typescript
   * const result = await client.validateAndVerify(
   *   cipherText,
   *   clientIP,
   *   "user_123",
   *   "registration"
   * );
   *
   * if (!result.ciphertext_valid) {
   *   throw new Error("Device verification failed");
   * }
   *
   * if (result.location.is_blocked) {
   *   throw new Error("Location not allowed");
   * }
   * ```
   */
  async validateAndVerify(
    cipherText: string,
    ipAddress: string,
    userId: string,
    eventType: CipherTextReason,
    requestOptions?: RequestOptions
  ): Promise<{
    ciphertext_valid: boolean;
    ciphertext_result: ValidateCipherTextResponse;
    location: LocationVerification;
  }> {
    // validate-ciphertext already performs full geo verification on the server
    // (IP lookup, risk scoring, alert triggering, device tracking). A separate
    // verifyIP call would create duplicate geolocation records and alerts.
    const cipherTextResult = await this.validateCipherText(
      cipherText,
      userId,
      eventType,
      ipAddress,
      undefined,
      requestOptions
    );

    // Build LocationVerification from the enriched ciphertext response.
    // If risk data is missing (partial response), fall back to verifyIP.
    const loc = cipherTextResult.location;
    const risk = cipherTextResult.risk;

    if (!risk) {
      const locationResult = await this.verifyIP({
        ip_address: ipAddress,
        user_id: userId,
        event_type: eventType,
      }, requestOptions);
      return {
        ciphertext_valid: cipherTextResult.valid,
        ciphertext_result: cipherTextResult,
        location: locationResult,
      };
    }

    const location: LocationVerification = {
      ip_address: cipherTextResult.ip_address,
      location: {
        country: loc?.country ?? '',
        country_iso: loc?.country_iso ?? '',
        city: loc?.city ?? '',
        region: loc?.region ?? '',
        postal_code: '',
        latitude: loc?.latitude ?? 0,
        longitude: loc?.longitude ?? 0,
        timezone: '',
        is_vpn: risk.is_vpn ?? false,
        is_proxy: risk.is_proxy ?? false,
        is_tor: risk.is_tor ?? false,
        is_hosting: risk.is_hosting ?? false,
        is_anonymizer: risk.is_vpn || risk.is_proxy || risk.is_tor,
      },
      is_compliant: cipherTextResult.is_compliant ?? !risk.is_blocked,
      is_blocked: risk.is_blocked ?? false,
      risk_level: risk.level ?? 'low',
      risk_score: risk.score ?? 0,
      risk_reasons: risk.is_blocked && risk.block_reasons
        ? risk.block_reasons
        : (risk.factors ?? []),
      jurisdiction: cipherTextResult.jurisdiction,
      geofence_evaluation: cipherTextResult.geofence_evaluation,
      record_id: cipherTextResult.record_id ?? '',
      gps_required: cipherTextResult.gps_required,
    };

    return {
      ciphertext_valid: cipherTextResult.valid,
      ciphertext_result: cipherTextResult,
      location,
    };
  }

  // ============================================================================
  // Location Request (Live Location Tracking)
  // ============================================================================

  /**
   * Create a location request to get customer's live location
   *
   * @param request - Location request details
   * @returns Location request result with share link
   *
   * @example
   * ```typescript
   * const result = await client.createLocationRequest({
   *   user_id: "customer_123",
   *   channel: "sms",
   *   phone: "+1234567890",
   *   reason: "Verification for high-value transaction"
   * });
   *
   * console.log(`Share link sent: ${result.share_link}`);
   * console.log(`Expires at: ${result.token_expiry}`);
   * ```
   */
  async createLocationRequest(
    request: CreateLocationRequestRequest,
    requestOptions?: RequestOptions
  ): Promise<LocationRequestResult> {
    return this.request<LocationRequestResult>('/api/v1/location-requests', {
      method: 'POST',
      body: JSON.stringify(request),
    }, undefined, requestOptions);
  }

  /**
   * Get a location request by ID
   *
   * @param requestId - Location request ID
   * @returns Location request details
   */
  async getLocationRequest(requestId: string, requestOptions?: RequestOptions): Promise<LocationRequest> {
    return this.request<LocationRequest>(`/api/v1/location-requests/${requestId}`, undefined, undefined, requestOptions);
  }

  /**
   * List location requests with filters and pagination
   *
   * @param filters - Optional filters
   * @param pagination - Optional pagination
   * @returns Paginated list of location requests
   *
   * @example
   * ```typescript
   * const requests = await client.listLocationRequests(
   *   { status: "completed", user_id: "customer_123" },
   *   { page: 1, limit: 20 }
   * );
   * ```
   */
  async listLocationRequests(
    filters?: LocationRequestFilters,
    pagination?: PaginationParams,
    requestOptions?: RequestOptions
  ): Promise<LocationRequestListResponse> {
    const params: Record<string, unknown> = {
      ...filters,
      ...pagination,
    };

    return this.request<LocationRequestListResponse>(
      `/api/v1/location-requests${this.buildQueryString(params)}`,
      undefined, undefined, requestOptions
    );
  }

  /**
   * Cancel a pending location request
   *
   * @param requestId - Location request ID
   */
  async cancelLocationRequest(requestId: string, requestOptions?: RequestOptions): Promise<void> {
    await this.request(`/api/v1/location-requests/${requestId}/cancel`, {
      method: 'POST',
    }, undefined, requestOptions);
  }

  /**
   * Resend notification for a location request
   *
   * @param requestId - Location request ID
   * @param contact - Email or phone to send to
   */
  async resendLocationRequest(
    requestId: string,
    contact: ResendLocationRequestRequest,
    requestOptions?: RequestOptions
  ): Promise<void> {
    await this.request(`/api/v1/location-requests/${requestId}/resend`, {
      method: 'POST',
      body: JSON.stringify(contact),
    }, undefined, requestOptions);
  }

  // ============================================================================
  // Location Share (Customer-facing - for SDK integration in customer apps)
  // ============================================================================

  /**
   * Get location share info (customer-facing)
   * This is called from the customer's device to get request details
   *
   * @param token - Secure token from share link
   * @returns Location share info
   */
  async getLocationShareInfo(token: string, requestOptions?: RequestOptions): Promise<LocationShareInfo> {
    return this.request<LocationShareInfo>(`/api/v1/location/share/${token}`, undefined, undefined, requestOptions);
  }

  /**
   * Submit location capture (customer-facing)
   * This is called from the customer's device to submit their location
   *
   * @param token - Secure token from share link
   * @param capture - Location capture data (GPS or WiFi)
   * @returns Capture response
   *
   * @example
   * ```typescript
   * // Using GPS (preferred)
   * const result = await client.captureLocation(token, {
   *   latitude: 37.7749,
   *   longitude: -122.4194,
   *   accuracy: 10
   * });
   *
   * // Using WiFi positioning (fallback)
   * const result = await client.captureLocation(token, {
   *   wifi_networks: [
   *     { macAddress: "00:11:22:33:44:55", signalStrength: -50 },
   *     { macAddress: "AA:BB:CC:DD:EE:FF", signalStrength: -70 }
   *   ]
   * });
   * ```
   */
  async captureLocation(
    token: string,
    capture: LocationCaptureRequest,
    requestOptions?: RequestOptions
  ): Promise<LocationCaptureResponse> {
    return this.request<LocationCaptureResponse>(`/api/v1/location/share/${token}`, {
      method: 'POST',
      body: JSON.stringify(capture),
    }, undefined, requestOptions);
  }

  // ============================================================================
  // CipherText Generation
  // ============================================================================

  /**
   * Generate a signed cipherText containing device and location data.
   *
   * Automatically passes the client's API key for HMAC signing (v02 format).
   * If no API key is configured, falls back to unsigned v01 format.
   *
   * @param options - Options for cipherText generation
   * @param gpsConfig - Optional GPS config (from getGPSConfig)
   * @returns CipherText result with the signed string
   *
   * @example
   * ```typescript
   * const result = await client.generateCipherText({ reason: 'login' });
   * console.log(result.cipherText); // v02 signed cipherText
   * ```
   */
  async generateCipherText(
    options: Omit<CipherTextOptions, 'apiKey' | 'signingKey'>,
    gpsConfig?: GeolocationConfigResponse,
  ): Promise<CipherTextResult> {
    // Use cached signing key, or fetch from dedicated endpoint (with fallback)
    let signingKey = this.cachedSigningKey;
    let resolvedGpsConfig = gpsConfig;
    if (!signingKey) {
      signingKey = await this.fetchSigningKey();
    }
    // Fetch GPS config for auto-enable when caller didn't provide one
    if (!resolvedGpsConfig) {
      resolvedGpsConfig = await this.getGPSConfig();
    }
    return generateCipherTextUtil(
      { ...options, signingKey: signingKey || undefined },
      resolvedGpsConfig,
    );
  }

  // ============================================================================
  // Utility Methods (inherited from BaseClient: healthCheck, updateConfig, getConfig, buildQueryString)
  // ============================================================================
}

/**
 * Auto-collect device fingerprint from the browser environment.
 * Returns safe defaults when running server-side (SSR/Node.js).
 */
function collectDeviceFingerprint(): VerifyIPRequest['device_fingerprint'] {
  const deviceId = generateDeviceId();
  const browserInfo = getBrowserInfo();
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'server';
  const platform = typeof navigator !== 'undefined' ? (navigator.platform || 'unknown') : 'server';

  return {
    device_id: deviceId,
    user_agent: userAgent,
    platform,
    browser: browserInfo.browser,
    browser_version: browserInfo.browser_version,
    os: browserInfo.os,
    os_version: browserInfo.os_version,
    screen_resolution: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}` : undefined,
    language: typeof navigator !== 'undefined' ? navigator.language : undefined,
    timezone: Intl?.DateTimeFormat?.()?.resolvedOptions?.()?.timeZone,
  };
}
